import React, { useMemo, useState } from "react";

// Material UI components
import { Box, CircularProgress, Divider, Typography, Button, Popover, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Custom Components
import WorkHistory from "./WorkHistory";
import ListPeople from "./ListPeople";
import CustomPopover from "components/Popover";
import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";
import LinkedInButton from "components/Team/LinkedInButton";
import { MemberPaceIcon } from "components/Team/MemberPaceIcon";
import { CompoundPill } from "components/CompoundPill";

// Hooks
import { FormattedMessage, useIntl } from "react-intl";
import usePersonWorkHistory from "./usePersonWorkHistory";
import usePopoverPosition from "components/Popover/usePopoverPosition";
import { useAppSelector } from "app/hooks";
import { getRoles } from "store/slice/Team";

// Types
import { TPersonWorkHistoryProps } from "./PersonWorkHistory.types";
import { PersonTalent } from "services/people/people.types";

// Utils
import { sortWorkHistoryAddFromTo } from "utils";
import { isEmpty } from "lodash";

// Styles
import { useStyles } from "./PersonWorkHistory.styles";

const PersonWorkHistory = React.forwardRef(function PersonWorkHistory(
    {
        role,
        personId,
        anchorEl,
        onClose,
        parentWidth,
        parentHeight,
        xElement,
        yElement,
        peopleIds,
        isBalanceChart,
        scrollTop,
        shouldHighlightPosition = false,
        open = false,
        isChart = false,
        functions,
        hierarchy,
    }: // isTeamChart = false,
    TPersonWorkHistoryProps,
    ref
) {
    const classes = useStyles();
    const intl = useIntl();
    const roles = useAppSelector(getRoles);

    const [showPerson, setShowPerson] = useState(false);

    const {
        personInfo: newPersonInfo,
        peopleInfo,
        setPersonInfo,
        requestStatus,
    } = usePersonWorkHistory(personId, setShowPerson, peopleIds, shouldHighlightPosition);

    const isLoading = requestStatus === "fetching";

    const {
        position,
        ref: refContainer,
        refHeader,
        refBody,
    } = usePopoverPosition(!!isBalanceChart, parentWidth, parentHeight, xElement, yElement, scrollTop);

    const workHistory = newPersonInfo?.work_history
        ? sortWorkHistoryAddFromTo(newPersonInfo.work_history, "tenure_from")
        : undefined;
    const isPeople = useMemo(() => Boolean(peopleInfo.length), [peopleInfo]);

    const resolvedFunction = (() => {
        if (functions && functions.length > 0) return functions;

        const roleData = roles.data.find((r) => r.name === role);
        return roleData?.function ? [roleData.function] : [];
    })();

    const resolvedHierarchy = (() => {
        if (hierarchy) return hierarchy;

        const roleData = roles.data.find((r) => r.name === role);
        return roleData?.hierarchy ?? null;
    })();

    const onClickPerson = (person: PersonTalent) => {
        setPersonInfo(person);
        setShowPerson(true);
    };

    const handleBackButton = () => {
        setPersonInfo(undefined);
        setShowPerson(false);
    };

    const onCloseHandler = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
        onClose();
    };

    return (
        <>
            {!isChart ? (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onCloseHandler}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    sx={{ "& .MuiPaper-root": { maxWidth: "500px" } }}
                >
                    {isLoading ? (
                        <Box display="flex" alignItems="center" justifyContent="center" width="200px" height="200px">
                            <CircularProgress color="primary" />
                        </Box>
                    ) : (
                        <Box minWidth={340}>
                            {/* Header */}
                            <Box className={classes.header}>
                                {newPersonInfo?.name ? (
                                    <>
                                        <Typography variant="overline" className={classes.headerName}>
                                            {newPersonInfo?.name.toUpperCase() ?? ""}
                                        </Typography>
                                        <IconButton
                                            aria-label="delete"
                                            className={classes.closeButton}
                                            onClick={onClose}
                                            size="large"
                                        >
                                            <CloseIcon className={classes.closeIcon} />
                                        </IconButton>
                                    </>
                                ) : (
                                    <Typography className={classes.headerInfoNA}>
                                        {intl.formatMessage({ id: "charts.popOver.incompleteProfile" })}
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                {newPersonInfo?.work_history?.length !== 0 &&
                                newPersonInfo?.hasOwnProperty("work_history") &&
                                open ? (
                                    <>
                                        <Box className={classes.subHeader} padding={0}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                {role ? (
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        Modelled as:
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                        Work History
                                                    </Typography>
                                                )}

                                                <Box display="flex">
                                                    <MemberPaceIcon member={newPersonInfo} />
                                                    <LinkedInButton member={newPersonInfo} />
                                                </Box>
                                            </Box>

                                            {role && (
                                                <Box display="flex" alignItems="center">
                                                    <Typography variant="body1" fontWeight={700}>
                                                        {role}
                                                    </Typography>
                                                    {(resolvedHierarchy !== null || resolvedFunction.length > 0) && (
                                                        <Divider
                                                            orientation="vertical"
                                                            flexItem
                                                            className={classes.headerDivider}
                                                        />
                                                    )}

                                                    <CompoundPill
                                                        functions={resolvedFunction}
                                                        hierarchy={resolvedHierarchy}
                                                    />
                                                </Box>
                                            )}
                                        </Box>

                                        {workHistory ? (
                                            <Box sx={{ maxHeight: "300px", overflow: "auto", paddingTop: 1 }}>
                                                <WorkHistory
                                                    shouldHighlightPosition={shouldHighlightPosition}
                                                    elements={workHistory}
                                                    maxHeight={position.maxHeight}
                                                />
                                            </Box>
                                        ) : null}

                                        <Box textAlign="right" marginBottom="20px" />
                                    </>
                                ) : open ? (
                                    <Typography paragraph className={classes.bodyInfoNA}>
                                        <FormattedMessage
                                            id="charts.popOver.notFound"
                                            values={{
                                                span: (...msg: React.ReactNode[]) => (
                                                    <span className={classes.feedbackButton}>{msg}</span>
                                                ),
                                            }}
                                        />
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    )}
                </Popover>
            ) : (
                <CustomPopover
                    ref={ref}
                    position={position}
                    refContainer={refContainer}
                    refHeader={refHeader}
                    refBody={refBody}
                    onClose={onCloseHandler}
                    isLoading={isLoading}
                    Header={
                        isPeople && !showPerson ? (
                            <Box className={classes.peopleHeader}>
                                <Typography variant="h5">
                                    {intl.formatMessage({ id: "charts.popOver.listOfPeople" })}
                                </Typography>
                            </Box>
                        ) : newPersonInfo?.name ? (
                            <Typography variant="overline" className={classes.headerName}>
                                {newPersonInfo?.name.toUpperCase() ?? ""}
                            </Typography>
                        ) : (
                            <Typography className={classes.headerInfoNA}>
                                {intl.formatMessage({ id: "charts.popOver.incompleteProfile" })}
                            </Typography>
                        )
                    }
                    Body={
                        isPeople && !showPerson ? (
                            peopleInfo ? (
                                <ListPeople
                                    elements={peopleInfo as PersonTalent[]}
                                    maxHeight={position.maxHeight}
                                    onClickPerson={onClickPerson}
                                />
                            ) : null
                        ) : !isEmpty(newPersonInfo) && newPersonInfo?.hasOwnProperty("work_history") ? (
                            <>
                                <Box className={classes.subHeader}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        {role ? (
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                Modelled as:
                                            </Typography>
                                        ) : (
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                Work History
                                            </Typography>
                                        )}

                                        <Box display="flex">
                                            <MemberPaceIcon member={newPersonInfo} />
                                            <LinkedInButton member={newPersonInfo} />
                                        </Box>
                                    </Box>

                                    {role && (
                                        <Box display="flex" alignItems="center">
                                            <Typography variant="body1" fontWeight={700}>
                                                {role}
                                            </Typography>
                                            {(resolvedHierarchy !== null || resolvedFunction.length > 0) && (
                                                <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                    className={classes.headerDivider}
                                                />
                                            )}

                                            <CompoundPill functions={resolvedFunction} hierarchy={resolvedHierarchy} />
                                        </Box>
                                    )}
                                </Box>
                                {isPeople && (
                                    <Box sx={{ position: "relative", left: "-14px" }}>
                                        <Button variant="text" size="small" onClick={handleBackButton}>
                                            <CustomIcon
                                                icon={icons.chevronLeftThin}
                                                sx={{ fontSize: 12, marginRight: 0.5 }}
                                            />
                                            <Typography variant="caption">Back To Team Board</Typography>
                                        </Button>
                                    </Box>
                                )}

                                {workHistory ? (
                                    <WorkHistory
                                        shouldHighlightPosition={shouldHighlightPosition}
                                        elements={workHistory}
                                        maxHeight={position.maxHeight}
                                    />
                                ) : null}

                                <Box textAlign="right" marginBottom="20px" />
                            </>
                        ) : (
                            <Typography paragraph className={classes.bodyInfoNA}>
                                <FormattedMessage
                                    id="charts.popOver.notFound"
                                    values={{
                                        span: (...msg: React.ReactNode[]) => (
                                            <span className={classes.feedbackButton}>{msg}</span>
                                        ),
                                    }}
                                />
                            </Typography>
                        )
                    }
                />
            )}
        </>
    );
});

export default PersonWorkHistory;
